import React, { useEffect, useRef, useState } from 'react';
import { Volume2, AlertCircle } from 'lucide-react';
import { slokaAudioUrl, meaningAudioUrl } from '../utils/mediaUrls';
import { useAudioVoice } from '../context/AudioVoiceContext';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';

function AudioPlayer({ label, src, compact = false }) {
  const audioRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoadError(false);
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, [src]);

  const handleError = () => {
    const code = audioRef.current?.error?.code;
    if (code === MediaError.MEDIA_ERR_ABORTED) {
      return;
    }
    setLoadError(true);
  };

  return (
    <div className={compact ? 'bg-orange-50/80 border border-orange-100 rounded-lg p-3' : ''}>
      {label && (
        <p
          className={`font-medium text-gita-maroon flex items-center ${
            compact ? 'text-xs mb-2' : 'text-sm mb-2'
          }`}
        >
          <Volume2 className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} mr-1.5`} />
          {label}
        </p>
      )}
      <audio
        ref={audioRef}
        controls
        preload="none"
        className="w-full"
        src={src}
        onError={handleError}
        onLoadedMetadata={() => setLoadError(false)}
      />
      {loadError && (
        <p className="mt-2 text-xs text-red-600 flex items-center">
          <AlertCircle className="w-3 h-3 mr-1 shrink-0" />
          Audio unavailable.{' '}
          <a href={src} target="_blank" rel="noopener noreferrer" className="underline ml-1">
            Open link
          </a>
        </p>
      )}
    </div>
  );
}

export function SlokaRecitationAudio({ chapterNumber, slokaNumber, compact = false }) {
  const { voice } = useAudioVoice();

  if (!chapterNumber || !slokaNumber) {
    return null;
  }

  return (
    <AudioPlayer
      label="Verse recitation"
      src={slokaAudioUrl(chapterNumber, slokaNumber, voice)}
      compact={compact}
    />
  );
}

export function MeaningRecitationAudio({
  language = DEFAULT_LANGUAGE,
  chapterNumber,
  slokaNumber,
  compact = false
}) {
  if (!chapterNumber || !slokaNumber) {
    return null;
  }

  return (
    <AudioPlayer
      label="Meaning narration"
      src={meaningAudioUrl(language, chapterNumber, slokaNumber)}
      compact={compact}
    />
  );
}

export default function VerseAudio({
  language = DEFAULT_LANGUAGE,
  chapterNumber,
  slokaNumber,
  compact = false
}) {
  if (compact) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SlokaRecitationAudio
          chapterNumber={chapterNumber}
          slokaNumber={slokaNumber}
          compact
        />
        <MeaningRecitationAudio
          language={language}
          chapterNumber={chapterNumber}
          slokaNumber={slokaNumber}
          compact
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SlokaRecitationAudio chapterNumber={chapterNumber} slokaNumber={slokaNumber} />
      <MeaningRecitationAudio
        language={language}
        chapterNumber={chapterNumber}
        slokaNumber={slokaNumber}
      />
    </div>
  );
}
